import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import { useProjectContext } from '~/modules/resourcing/common/contexts';
import {
  ResourceRequestUserAllocationBlock,
  ResourceAllocationPeriodEditPopoverDetails
} from '~/modules/resourcing/common/components';
import { RangeBoundaries } from '~/modules/common/charts/timeline/components';
import { useUpdateResourceAllocation } from '~/modules/resourcing/hooks';
import usePeriodEditorChangeHandlers from '~/modules/resourcing/common/hooks/usePeriodEditorChangeHandlers';
import { useDeleteResourceAllocation } from '~/modules/resourcing/hooks/useDeleteResourceAllocation';
import {
  useResourceActionsDialogContext,
  useResourceRequestToolbarContext
} from '~/modules/projects/resourcing-plan/hooks';
import { useReleaseResourceAllocation } from '~/modules/resourcing/common/hooks';
import { useSessionStorage } from '~/modules/common/hooks';

import {
  useAvailabilityContext,
  withAvailabilityContextProvider
} from '~/modules/resourcing/common/hooks/useAvailabilityContext';
import ResourceActualHoursSummaryBlocks from '~/modules/resourcing/common/components/ResourceActualHoursSummaryBlocks/ResourceActualHoursSummaryBlocks';
import { useMeContext } from '~/modules/me';
import { useOverlayTimelineBlocks } from '~/modules/resourcing/common/chart/hooks';
import { deepPure } from '~/util';
import { getFilterInput } from '../../hooks/useCompletedProjectResourceAllocations';
import { useUpdateCompletedResourceAllocationProjectCacheOnDelete } from '../../hooks/useUpdateCompletedResourceAllocationCache';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(1),
    minHeight: theme.spacing(5)
  }
}));

const useActualStyles = makeStyles(theme => ({
  root: {
    minHeight: theme.spacing(10)
  }
}));

export const ResourceAllocationChartTimeLineItem = ({
  resourceAllocation,
  resourceUserActualSeries,
  isEditEnabled
}) => {
  const me = useMeContext();
  const {
    resourceCostMode,
    featureFlags: { isPSARMPResourceActualEnabled }
  } = me;

  const {
    scale,
    chartDates,
    dateRange: chartDisplayDateRange,
    isResourceActualModeEnabled,
    filter,
    sort
  } = useResourceRequestToolbarContext();
  const classes = useStyles();
  const actualClasses = useActualStyles();

  const [{ start: chartStartDate }] = chartDates;
  const { setResourceActionContextDetails, openReleaseResourcesDialog } =
    useResourceActionsDialogContext() || {};
  const { project } = useProjectContext();
  const { setAvailabilityLoading } = useAvailabilityContext();
  const [allocationPeriodEditTarget, setAllocationPeriodEditTarget] = useState(
    null
  );

  const { startDate, endDate } = project;

  const { onPatchResourceAllocation } = useUpdateResourceAllocation();
  const deleteResourceAllocationFromProject = useDeleteResourceAllocation({
    projectUri: project.id,
    resourceRequestId: resourceAllocation.resourceRequestId
  });

  const { storedValue } = useSessionStorage(
    'RESOURCE-ALLOCATIONS-QUERY-VARIABLES',
    null
  );

  const { releaseResourceAllocation } = useReleaseResourceAllocation({
    resourceAllocation,
    updateDeleteCache: useUpdateCompletedResourceAllocationProjectCacheOnDelete(
      {
        projectUri: project.id,
        limit: 50,
        chartDateRange: storedValue.chartDateRange || {},
        showTimeOff: storedValue.showTimeOff,
        showHolidays: storedValue.showHolidays,
        filter: getFilterInput(filter),
        sort,
        resourceCostMode
      }
    )
  });

  const onPeriodClose = useCallback(
    (evt, originalAllocation) => {
      setAvailabilityLoading();
      onPatchResourceAllocation({
        project,
        openReleaseResourcesDialog,
        deleteResourceAllocationFromProject,
        allocation: {
          ...evt.values,
          requestedRoleUri: resourceAllocation.role?.uri
        },
        originalAllocation
      });
    },
    [
      onPatchResourceAllocation,
      project,
      openReleaseResourcesDialog,
      deleteResourceAllocationFromProject
    ]
  );

  const onAllocationPeriodClick = useCallback(() => {
    setResourceActionContextDetails({
      resourceRequestId: resourceAllocation.resourceRequestId,
      resourceAllocation,
      releaseResourceAllocation
    });
  }, [
    setResourceActionContextDetails,
    releaseResourceAllocation,
    resourceAllocation.resourceRequestId
  ]);

  const {
    handleAllocationPeriodClick,
    handleAllocationPeriodClose,
    setPreviousPeriod,
    setNextPeriod
  } = usePeriodEditorChangeHandlers({
    allocationPeriodEditTarget,
    onPeriodClick: onAllocationPeriodClick,
    chartDates,
    setAllocationPeriodEditTarget
  });

  const { overlayPeriods: allocationPeriods } = useOverlayTimelineBlocks({
    chartDisplayDateRange,
    scale,
    showTotalCost: true,
    allocation: resourceAllocation
  });

  return (
    <div
      className={classNames(classes.root, {
        [actualClasses.root]: isResourceActualModeEnabled
      })}
    >
      <RangeBoundaries
        chartStartDate={chartStartDate}
        scale={scale}
        start={startDate}
        end={endDate}
      />
      <ResourceRequestUserAllocationBlock
        scale={scale}
        isEditable={isEditEnabled}
        showRequestBlock={false}
        chartDisplayDateRange={chartDisplayDateRange}
        projectDefaultScheduleRule={project.defaultScheduleRule}
        allocation={resourceAllocation}
        handleAllocationPeriodClick={handleAllocationPeriodClick}
        onAllocationChange={onPeriodClose}
        showResizeIndicator={isEditEnabled}
        overlayPeriods={allocationPeriods}
      />
      {isEditEnabled &&
        allocationPeriodEditTarget &&
        allocationPeriodEditTarget.anchorEl && (
          <ResourceAllocationPeriodEditPopoverDetails
            chartStartDate={chartStartDate}
            chartEndDate={chartDates[chartDates.length - 1].end}
            scale={scale}
            allocationPeriodEditDetails={allocationPeriodEditTarget}
            onAllocationChange={onPeriodClose}
            isAllocationComplete
            handleAllocationPeriodClose={handleAllocationPeriodClose}
            setNextPeriod={setNextPeriod}
            setPreviousPeriod={setPreviousPeriod}
          />
        )}
      {isPSARMPResourceActualEnabled && isResourceActualModeEnabled && (
        <ResourceActualHoursSummaryBlocks
          dataQeId="AllocationRow"
          chartStartDate={chartStartDate}
          scale={scale}
          resourceUser={resourceAllocation.user.user}
          resourceUserActualTimePeriods={
            resourceUserActualSeries && resourceUserActualSeries.periods
          }
          allocationPeriods={allocationPeriods}
        />
      )}
    </div>
  );
};

ResourceAllocationChartTimeLineItem.propTypes = {
  resourceAllocation: PropTypes.object.isRequired,
  resourceUserActualSeries: PropTypes.object,
  isEditEnabled: PropTypes.bool
};

export default deepPure(
  withAvailabilityContextProvider(ResourceAllocationChartTimeLineItem)
);
